import { PaymentMethod } from '../../types/index';

/** Linha de pagamento vazia para formulários. */
export function criarLinhaPagamento(overrides = {}) {
  return {
    method: PaymentMethod.CASH,
    value: '',
    paymentDate: '',
    paymentTime: '',
    ...overrides
  };
}

/** Duas linhas padrão ao ativar pagamento misto. */
export function pagamentosDefaultMisto() {
  return [
    criarLinhaPagamento(),
    criarLinhaPagamento({ method: PaymentMethod.CARD })
  ];
}

/**
 * Normaliza um movimento para array de pagamentos.
 * Suporta o novo campo `payments` e o formato legado (paymentMethod/value1/value2).
 */
export function pagamentosDoMovimento(movimento) {
  if (!movimento) return [];

  if (movimento.payments && Array.isArray(movimento.payments) && movimento.payments.length > 0) {
    return movimento.payments.map(p => ({
      method: p.method,
      value: Number(p.value || 0),
      paymentDate: p.paymentDate || '',
      paymentTime: p.paymentTime || ''
    }));
  }

  if (movimento.isMixedPayment) {
    const result = [];
    if (movimento.paymentMethod) {
      result.push({
        method: movimento.paymentMethod,
        value: Number(movimento.value1 || 0),
        paymentDate: movimento.paymentDate1 || movimento.date || '',
        paymentTime: movimento.paymentTime1 || ''
      });
    }
    if (movimento.paymentMethod2) {
      result.push({
        method: movimento.paymentMethod2,
        value: Number(movimento.value2 || 0),
        paymentDate: movimento.paymentDate2 || movimento.date || '',
        paymentTime: movimento.paymentTime2 || ''
      });
    }
    return result;
  }

  if (movimento.paymentMethod) {
    return [{
      method: movimento.paymentMethod,
      value: Number(movimento.value || 0),
      paymentDate: movimento.paymentDate1 || movimento.date || '',
      paymentTime: movimento.paymentTime1 || ''
    }];
  }

  return [];
}

/** Verifica se o movimento usa um determinado método de pagamento. */
export function movimentoUsaMetodo(movimento, metodo) {
  if (!metodo || !movimento) return false;
  if (!movimento.isMixedPayment) {
    return movimento.paymentMethod === metodo;
  }
  return pagamentosDoMovimento(movimento).some(p => p.method === metodo);
}

/**
 * Devolve quanto desse movimento pertence a um método,
 * suportando pagamento misto com N métodos.
 */
export function valorPorMetodoNoMovimento(movimento, metodo) {
  if (!movimento || !metodo) return 0;

  if (!movimento.isMixedPayment) {
    return movimento.paymentMethod === metodo ? Number(movimento.value || 0) : 0;
  }

  return pagamentosDoMovimento(movimento)
    .filter(p => p.method === metodo)
    .reduce((acc, p) => acc + p.value, 0);
}

/** Soma quanto uma lista de movimentos tem num determinado método. */
export function somarPorMetodo(movimentos, metodo) {
  return (movimentos || []).reduce((acc, m) => acc + valorPorMetodoNoMovimento(m, metodo), 0);
}

/** Verifica se o movimento tem algum valor pendente. */
export function movimentoTemPendente(movimento) {
  return pagamentosDoMovimento(movimento).some(p => p.method === PaymentMethod.PENDING);
}

/** Soma o valor pendente de um movimento. */
export function dividaPendenteMovimento(movimento) {
  return pagamentosDoMovimento(movimento)
    .filter(p => p.method === PaymentMethod.PENDING)
    .reduce((acc, p) => acc + p.value, 0);
}

/** Soma valores de linhas de pagamento no formulário. */
export function valorTotalPagamentosForm(payments) {
  return (payments || []).reduce((acc, p) => acc + Number(p.value || 0), 0);
}

/** Converte pagamentos do formulário para payload. */
export function normalizarPagamentosParaPayload(payments) {
  return (payments || [])
    .filter(p => p.method && p.value !== '' && p.value !== null && p.value !== undefined)
    .map(p => ({
      method: p.method,
      value: Number(p.value || 0),
      paymentDate: p.paymentDate || undefined,
      paymentTime: p.paymentTime || undefined
    }));
}

/** Sincroniza array payments para campos legacy (primeiros 2). */
export function sincronizarCamposLegadosPagamentos(payments) {
  const normalized = normalizarPagamentosParaPayload(payments);
  const p1 = normalized[0];
  const p2 = normalized[1];

  return {
    payments: normalized,
    paymentMethod: p1?.method,
    paymentMethod2: p2?.method,
    value1: p1?.value,
    value2: p2?.value,
    paymentDate1: p1?.paymentDate,
    paymentDate2: p2?.paymentDate,
    paymentTime1: p1?.paymentTime,
    paymentTime2: p2?.paymentTime
  };
}

/** Preenche form.payments a partir de um movimento existente. */
export function formPagamentosFromMovimento(movimento) {
  if (!movimento?.isMixedPayment) {
    return pagamentosDefaultMisto();
  }

  const pagamentos = pagamentosDoMovimento(movimento);
  if (pagamentos.length === 0) {
    return pagamentosDefaultMisto();
  }

  return pagamentos.map(p => {
    let paymentDate = p.paymentDate || '';
    let paymentTime = p.paymentTime || '';
    if (paymentDate.includes('T')) {
      const [datePart, timePart] = paymentDate.split('T');
      paymentDate = datePart;
      if (!paymentTime && timePart) {
        paymentTime = timePart.substring(0, 5);
      }
    }
    return {
      method: p.method,
      value: String(p.value ?? ''),
      paymentDate,
      paymentTime
    };
  });
}

/** Texto resumo para pagamento misto. */
export function formatarResumoPagamentoMisto(movimento) {
  if (!movimento?.isMixedPayment) {
    return traduzirMetodoPagamento(movimento?.paymentMethod);
  }
  return pagamentosDoMovimento(movimento)
    .map(p => `${traduzirMetodoPagamento(p.method)}: ${p.value.toFixed(2)}€`)
    .join(' + ');
}

/** Troco (nunca negativo). */
export function calcularTroco(valorRecebido, total) {
  const received = Number(valorRecebido || 0);
  const tot = Number(total || 0);
  const diff = received - tot;
  return diff > 0 ? diff : 0;
}

/** Traduz método de pagamento para português. */
export function traduzirMetodoPagamento(metodo) {
  switch (metodo) {
    case PaymentMethod.CASH:
      return 'Dinheiro';
    case PaymentMethod.CARD:
      return 'Multibanco';
    case PaymentMethod.TRANSFER:
      return 'Transferência';
    case PaymentMethod.CHEQUE:
      return 'Cheque';
    case PaymentMethod.PENDING:
      return 'Pendente';
    case PaymentMethod.VOUCHER:
      return 'Voucher';
    case PaymentMethod.MBWAY:
      return 'MBWay';
    default:
      return metodo;
  }
}
